import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import i18n from '../i18n';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * QR Loyalty & Ordering — owner management API (Phase 2). Gated behind
 * check.feature:qr_loyalty on the backend (402 → FeatureLocked via FeatureGate).
 * Endpoints return plain JSON (not JsonResource), so `res?.data ?? res` is a
 * safe no-op unwrap that also survives if wrapping is ever turned on.
 *
 * Toasts are localized via i18n.t() (read at fire time so they follow the live
 * language). Known backend error codes (venue_code_taken / invalid_venue_code)
 * map to specific localized messages; everything else falls back to a generic
 * localized message.
 */

const SETTINGS_KEY  = 'loyaltySettings';
const VENUECODE_KEY = 'loyaltyVenueCode';
const REWARDS_KEY   = 'loyaltyRewards';
const FEATURED_KEY  = 'loyaltyFeatured';
const ANALYTICS_KEY = 'loyaltyAnalytics';
const TVDEVICES_KEY = 'loyaltyTvDevices';

const unwrap = (res) => res?.data ?? res;
const T = (k) => i18n.t(`loyalty.toast.${k}`);

/* ── analytics (Phase 5, read-only owner dashboard) ───────────────────────── */
export const useLoyaltyAnalytics = () =>
  useQuery(ANALYTICS_KEY, async () => unwrap((await axiosInstance.get('/loyalty/analytics')).data), {
    // Freshen when the owner returns to the tab; the roll-up is cheap.
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

/* ── settings ─────────────────────────────────────────────────────────────── */
export const useLoyaltySettings = () =>
  useQuery(SETTINGS_KEY, async () => unwrap((await axiosInstance.get('/loyalty/settings')).data));

export const useUpdateLoyaltySettings = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((body) => axiosInstance.put('/loyalty/settings', body).then((r) => unwrap(r.data)), {
    onSuccess: () => { qc.invalidateQueries(SETTINGS_KEY); showToast('success', T('title_loyalty'), T('settings_saved')); },
    onError: () => showToast('error', T('title_loyalty'), T('err_settings')),
  });
};

/* ── Scan & Stay TV overlays ──────────────────────────────────────────────────
 * Manual controls for the three player overlays. The periodic/dayparted pushes
 * run unattended server-side; these are the "now" buttons. Each returns
 * { ok, devices } (devices = screens reached). Featured show/hide also flips the
 * persisted toggle, so we refresh the settings cache. */
export const useTvFeaturedShow = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    (featured_item_id) =>
      axiosInstance
        .post('/loyalty/tv/featured/show', featured_item_id ? { featured_item_id } : {})
        .then((r) => unwrap(r.data)),
    {
      onSuccess: (res) => {
        qc.invalidateQueries(SETTINGS_KEY);
        // The list carries is_showing / is_pinned, so its badges go stale the
        // moment a push lands — refresh it alongside the settings cache.
        qc.invalidateQueries(FEATURED_KEY);
        showToast('success', T('title_loyalty'), i18n.t('loyalty.tv.toast_shown', { count: res?.devices ?? 0 }));
      },
      onError: (e) => {
        const code = e?.response?.data?.error;
        showToast('error', T('title_loyalty'),
          code === 'no_featured_item' ? i18n.t('loyalty.tv.toast_no_item') : T('err_settings'));
      },
    }
  );
};

export const useTvFeaturedHide = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(() => axiosInstance.post('/loyalty/tv/featured/hide').then((r) => unwrap(r.data)), {
    onSuccess: (res) => {
      qc.invalidateQueries(SETTINGS_KEY);
      qc.invalidateQueries(FEATURED_KEY);
      showToast('success', T('title_loyalty'), i18n.t('loyalty.tv.toast_hidden', { count: res?.devices ?? 0 }));
    },
    onError: () => showToast('error', T('title_loyalty'), T('err_settings')),
  });
};

/**
 * Release a manual "show this one" pin and hand the screens back to the
 * rotation. Leaves the spotlight enabled — this is "stop holding one item",
 * not "turn the feature off".
 */
export const useTvFeaturedAuto = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(() => axiosInstance.post('/loyalty/tv/featured/auto').then((r) => unwrap(r.data)), {
    onSuccess: (res) => {
      qc.invalidateQueries(SETTINGS_KEY);
      qc.invalidateQueries(FEATURED_KEY);
      showToast('success', T('title_loyalty'), i18n.t('loyalty.tv.toast_auto', { count: res?.devices ?? 0 }));
    },
    onError: () => showToast('error', T('title_loyalty'), T('err_settings')),
  });
};

export const useTvWelcomeTest = () => {
  const { showToast } = useCustomToast();
  return useMutation(() => axiosInstance.post('/loyalty/tv/welcome/test').then((r) => unwrap(r.data)), {
    onSuccess: (res) =>
      showToast('success', T('title_loyalty'), i18n.t('loyalty.tv.toast_welcome_sent', { count: res?.devices ?? 0 })),
    onError: () => showToast('error', T('title_loyalty'), T('err_settings')),
  });
};

export const useTvCelebrationTest = () => {
  const { showToast } = useCustomToast();
  return useMutation(() => axiosInstance.post('/loyalty/tv/celebration/test').then((r) => unwrap(r.data)), {
    onSuccess: (res) =>
      showToast('success', T('title_loyalty'), i18n.t('loyalty.tv.toast_celebration_sent', { count: res?.devices ?? 0 })),
    onError: () => showToast('error', T('title_loyalty'), T('err_settings')),
  });
};

/* ── P5e · greeter screens ────────────────────────────────────────────────────
 * The venue's screens plus which of them are greeters. A greeter is a ROLE an
 * existing screen takes on, so this reads the ordinary device list rather than
 * a Scan & Stay-specific one.
 */
export const useTvDevices = (enabled = true) =>
  useQuery(TVDEVICES_KEY, async () => unwrap((await axiosInstance.get('/loyalty/tv/devices')).data), {
    enabled,
    // The row shows live online state next to the toggle, so an owner can tell
    // "pinned" from "pinned, but that screen is unplugged".
    refetchOnWindowFocus: true,
  });

export const useSetGreeter = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    ({ id, greeter }) =>
      axiosInstance.put(`/loyalty/tv/devices/${id}/greeter`, { greeter }).then((r) => unwrap(r.data)),
    {
      onSuccess: (res) => {
        qc.invalidateQueries(TVDEVICES_KEY);
        showToast(
          'success',
          T('title_loyalty'),
          i18n.t(res?.greeter ? 'loyalty.tv.toast_greeter_on' : 'loyalty.tv.toast_greeter_off')
        );
      },
      onError: () => showToast('error', T('title_loyalty'), T('err_settings')),
    }
  );
};

export const useRefreshGreeters = () => {
  const { showToast } = useCustomToast();
  return useMutation(() => axiosInstance.post('/loyalty/tv/greeter/refresh').then((r) => unwrap(r.data)), {
    onSuccess: (res) =>
      showToast('success', T('title_loyalty'), i18n.t('loyalty.tv.toast_greeter_refresh', { count: res?.devices ?? 0 })),
    onError: () => showToast('error', T('title_loyalty'), T('err_settings')),
  });
};

/* ── venue code ───────────────────────────────────────────────────────────── */
export const useVenueCode = () =>
  useQuery(VENUECODE_KEY, async () => unwrap((await axiosInstance.get('/loyalty/venue-code')).data));

export const useUpdateVenueCode = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((venue_code) => axiosInstance.put('/loyalty/venue-code', { venue_code }).then((r) => unwrap(r.data)), {
    onSuccess: () => { qc.invalidateQueries(VENUECODE_KEY); showToast('success', T('title_loyalty'), T('venue_updated')); },
    onError: (e) => {
      const code = e?.response?.data?.error;
      const key = code === 'venue_code_taken' ? 'err_venue_taken'
        : code === 'invalid_venue_code' ? 'err_venue_invalid'
        : 'err_venue';
      showToast('error', T('title_loyalty'), T(key));
    },
  });
};

/* ── rewards ──────────────────────────────────────────────────────────────── */
export const useLoyaltyRewards = () =>
  useQuery(REWARDS_KEY, async () => unwrap((await axiosInstance.get('/loyalty/rewards')).data) || []);

export const useSaveReward = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    ({ id, ...body }) =>
      (id ? axiosInstance.put(`/loyalty/rewards/${id}`, body) : axiosInstance.post('/loyalty/rewards', body)).then((r) => unwrap(r.data)),
    {
      onSuccess: () => { qc.invalidateQueries(REWARDS_KEY); showToast('success', T('title_rewards'), T('reward_saved')); },
      onError: () => showToast('error', T('title_rewards'), T('err_reward')),
    }
  );
};

export const useDeleteReward = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/loyalty/rewards/${id}`), {
    onSuccess: () => { qc.invalidateQueries(REWARDS_KEY); showToast('success', T('title_rewards'), T('reward_deleted')); },
    onError: () => showToast('error', T('title_rewards'), T('err_reward_delete')),
  });
};

export const useReorderRewards = () => {
  const qc = useQueryClient();
  return useMutation((order) => axiosInstance.put('/loyalty/rewards/order', { order }), {
    onSuccess: () => qc.invalidateQueries(REWARDS_KEY),
  });
};

/* ── featured items ───────────────────────────────────────────────────────── */
export const useFeaturedItems = () =>
  useQuery(FEATURED_KEY, async () => unwrap((await axiosInstance.get('/loyalty/featured-items')).data) || []);

export const useSaveFeatured = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    ({ id, ...body }) =>
      (id ? axiosInstance.put(`/loyalty/featured-items/${id}`, body) : axiosInstance.post('/loyalty/featured-items', body)).then((r) => unwrap(r.data)),
    {
      onSuccess: () => { qc.invalidateQueries(FEATURED_KEY); showToast('success', T('title_featured'), T('item_saved')); },
      onError: () => showToast('error', T('title_featured'), T('err_item')),
    }
  );
};

export const useDeleteFeatured = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/loyalty/featured-items/${id}`), {
    onSuccess: () => { qc.invalidateQueries(FEATURED_KEY); showToast('success', T('title_featured'), T('item_deleted')); },
    onError: () => showToast('error', T('title_featured'), T('err_item_delete')),
  });
};
